import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
} from 'recharts'
import { useFirestoreArrayContainsOrderBy } from '../../firebase/useFirestore'
import useHierarchyData from '../../hooks/useHierarchyData'
import buildPrePostScatter from '../../helpers/analysisHelpers/buildPrePostScatter'
import partitionPrePostPoints from '../../helpers/analysisHelpers/partitionPrePostPoints'
import summarizeScatterForAI from '../../helpers/analysisHelpers/summarizeScatterForAI'
import DeepAnalysisAIContext from '../../StateManagment/DeepAnalysisAIContext'
import DeepAnalysisPeriodContext from '../../StateManagment/DeepAnalysisPeriodContext'
import ComparisonFrame from '../dataVisualizations/ComparisonFrame'
import periodRangeToMs from '../../helpers/compareHelpers/periodRangeToMs'
import sideSummariesForAI from '../../helpers/compareHelpers/sideSummariesForAI'
import DeepAnalysisSectionHeader from './DeepAnalysisSectionHeader'
import { CATEGORICAL } from '../../helpers/chartPalette'
import useChartColors from '../../hooks/useChartColors'

// Grown respondents get the semantic growth green (redesign), the rest the
// tenant's primary colour. Keep in sync with the legend colours in
// src/CSS/analysis/deepAnalysis.css (.deep-analysis-scatter-legend-item--grown
// / .deep-analysis-scatter-line).
// Groei kreeg ooit een eigen groen dat nergens anders in de app voorkomt.
// Nu de merk-teal, zodat deze grafiek bij de rest hoort.
const GROWTH_GREEN = CATEGORICAL[0]
const DIAGONAL_GREY = '#c3c7d2'

/**
 * One period's scatter: fetches the indicator's records within `range`
 * (null = all data), draws the pre×post plot and reports its summary for
 * Decca through `onSummary` (undefined while not ready).
 *
 * @param {object} props
 * @param {object} props.indicator - The QuestionnaireFields doc to plot
 * @param {{start: string|null, end: string|null}|null} props.range - Period to fetch (null = all data)
 * @param {'A'|'B'} props.side - Which period this is
 * @param {(side: 'A'|'B', summary: object|undefined) => void} props.onSummary - Receives the Decca summary once loaded
 */
const ScatterPanel = ({ indicator, range, side, onSummary }) => {
    const { palette } = useChartColors()
    // Paletpositie, geen huisstijlkleur — zie docs/patterns.md#ui-chart-palette.
    const [kleurDeelnemer] = palette

    // Mirrors IndicatorData's syncType choice: MSI matrix indicators sync as
    // an MSIEffect bundle, plain questions as Question rows.
    const isMsiMatrix = (indicator.Type === 'matrix-one' || indicator.Type === 'matrix-multiple')
        && indicator.Standard === 'true'

    const { startMs, endMs } = periodRangeToMs(range)
    const { records, totalRespondents, loading, error } = useHierarchyData({
        syncType: isMsiMatrix ? 'MSIEffect' : 'Question',
        id: indicator.ID,
        startDate: startMs,
        endDate: endMs,
    })

    const points = useMemo(() => buildPrePostScatter(records), [records])
    const { grown, other } = useMemo(() => partitionPrePostPoints(points), [points])

    useEffect(() => {
        onSummary(side, loading || error ? undefined : summarizeScatterForAI(points, totalRespondents))
    }, [onSummary, side, loading, error, points, totalRespondents])

    // Diagonal reference (x = y): points above it grew. Domain from the data.
    const maxScore = points.reduce((max, p) => Math.max(max, p.x, p.y), 0)

    if (loading) return <p>Gegevens worden geladen…</p>
    if (error) {
        return (
            <p className='deep-analysis-error'>
                De gegevens konden niet worden geladen: {String(error.message || error)}
            </p>
        )
    }
    if (points.length === 0) {
        // With a period the likely cause is the period itself, not the
        // tracking module — say which. AI-CONTEXT: See docs/features/deep-analysis-period.md
        return (
            <p className='deep-analysis-empty'>
                {range
                    ? 'Te weinig metingen in deze periode: geen deelnemers die binnen deze periode op meerdere meetmomenten antwoordden.'
                    : 'Geen koppelbare deelnemers gevonden. Deze grafiek vereist deelnemers die op meerdere meetmomenten aan hun antwoorden gekoppeld zijn (via de deelnemer-volgmodule).'}
            </p>
        )
    }

    return (
        <>
            <div className='deep-analysis-scatter'>
                <ResponsiveContainer width='100%' height={320}>
                    <ScatterChart margin={{ top: 10, right: 20, bottom: 24, left: 12 }}>
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis
                            type='number'
                            dataKey='x'
                            name='Eerste meting'
                            domain={[0, Math.ceil(maxScore)]}
                            label={{ value: 'Score eerste meting', position: 'insideBottom', offset: -14, className: 'deep-analysis-axis-label' }}
                        />
                        <YAxis
                            type='number'
                            dataKey='y'
                            name='Laatste meting'
                            domain={[0, Math.ceil(maxScore)]}
                            label={{ value: 'Score laatste meting', angle: -90, position: 'insideLeft', className: 'deep-analysis-axis-label' }}
                        />
                        <ReferenceLine
                            segment={[{ x: 0, y: 0 }, { x: Math.ceil(maxScore), y: Math.ceil(maxScore) }]}
                            stroke={DIAGONAL_GREY}
                            strokeDasharray='4 4'
                        />
                        <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                        <Scatter data={other} fill={kleurDeelnemer} />
                        <Scatter data={grown} fill={GROWTH_GREEN} />
                    </ScatterChart>
                </ResponsiveContainer>
            </div>
            <div className='deep-analysis-scatter-legend'>
                <span className='deep-analysis-scatter-legend-item'>
                    {/* SVG fill zodat het blokje dezelfde paletkleur volgt als de punten. */}
                    <svg className='deep-analysis-scatter-swatch' viewBox='0 0 12 12' aria-hidden='true'>
                        <circle cx='6' cy='6' r='5.5' fill={kleurDeelnemer} />
                    </svg>
                    Deelnemer
                </span>
                <span className='deep-analysis-scatter-legend-item'>
                    <span className='deep-analysis-scatter-line' />
                    Gelijk gebleven (x = y)
                </span>
                <span className='deep-analysis-scatter-legend-item deep-analysis-scatter-legend-item--grown'>
                    ▲ boven de lijn = gegroeid
                </span>
            </div>
            <p className='deep-analysis-scatter-count'>
                <b>{points.length}</b> deelnemers met meerdere metingen
                {totalRespondents ? <> (van <b>{totalRespondents}</b> respondenten)</> : ''}
            </p>
        </>
    )
}

/**
 * Per-respondent pre×post scatterplot for one effect's indicator.
 * X = score at the respondent's first measured moment, Y = the latest moment.
 * Points above the diagonal grew (green), points on/below it did not (teal).
 *
 * Cross-moment pairing keys on Persona (FormID is per submission), so only
 * persona-linked respondents can appear — the section says so when empty.
 *
 * Follows the page period (DeepAnalysisPeriodContext); when two periods are
 * compared it draws one scatter per period side by side.
 *
 * @param {object} props
 * @param {object} props.effect - The Effect doc this deep analysis is anchored on
 */
const EffectScatterSection = ({ effect }) => {
    const [selectedIndicatorId, setSelectedIndicatorId] = useState('')
    const period = useContext(DeepAnalysisPeriodContext)
    const compare = Boolean(period?.compareActive)

    const questionnaireFields = useFirestoreArrayContainsOrderBy(
        'QuestionnaireFields',
        'EffectId',
        effect.ID || '123',
        'IndicatorPosition',
        'asc'
    )
    const indicators = useMemo(
        () => (Array.isArray(questionnaireFields) ? questionnaireFields : []),
        [questionnaireFields]
    )
    const indicator = indicators.find((f) => f.ID === selectedIndicatorId) || indicators[0]

    // One summary per period; Decca gets them apart (sideSummariesForAI).
    const [summaries, setSummaries] = useState(/** @type {{A?: object, B?: object}} */ ({}))
    const onSummary = useCallback((side, summary) => {
        // Equality guard on content (like `publish`): a fresh but equal
        // summary object must not trigger another render.
        setSummaries((prev) => (
            JSON.stringify(prev[side]) === JSON.stringify(summary) ? prev : { ...prev, [side]: summary }
        ))
    }, [])

    // Publish a compact summary for Decca (deep-analysis AI section).
    const aiContext = useContext(DeepAnalysisAIContext)
    const publish = aiContext?.publish
    const forAI = useMemo(() => sideSummariesForAI(summaries, period), [summaries, period])
    useEffect(() => {
        if (!publish || !forAI) return
        publish('spreidingPerDeelnemer', forAI)
    }, [publish, forAI])

    if (!indicator) return null

    return (
        <div className='deep-analysis-section'>
            <DeepAnalysisSectionHeader
                kicker='Spreiding'
                title='Spreiding per deelnemer'
                description='Iedere stip is één deelnemer: naar rechts de score bij de eerste meting, omhoog de score bij de laatste meting. Stippen boven de lijn zijn gegroeid.'
            />
            {indicators.length > 1 && (
                <select
                    className='deep-analysis-indicator-select'
                    value={indicator.ID}
                    onChange={(e) => setSelectedIndicatorId(e.target.value)}
                >
                    {indicators.map((f) => (
                        <option key={f.ID} value={f.ID}>{f.Question || 'Vraag'}</option>
                    ))}
                </select>
            )}
            {compare ? (
                <ComparisonFrame
                    rangeA={period.rangeA}
                    rangeB={period.rangeB}
                    renderViz={(range, side) => (
                        <ScatterPanel indicator={indicator} range={range} side={side} onSummary={onSummary} />
                    )}
                />
            ) : (
                <ScatterPanel indicator={indicator} range={period?.rangeA || null} side='A' onSummary={onSummary} />
            )}
        </div>
    )
}

export default EffectScatterSection
